import posthog from 'posthog-js';
import { track } from '@vercel/analytics';
import { 
  trackTikTokViewContent, 
  trackTikTokCompleteRegistration,
  trackTikTokLead,
  trackTikTokContact 
} from './tiktokTracking.js';

// Check if we're in production environment
const shouldTrackAnalytics = () => {
  return import.meta.env.VITE_PUBLIC_APP_ENV === 'production';
};

export const trackPostHog = (eventName, properties = {}) => {
  if (shouldTrackAnalytics() && typeof window !== 'undefined' && posthog) {
    posthog.capture(eventName, properties);
  }
};

export const trackVercel = (eventName, properties = {}) => {
  if (shouldTrackAnalytics() && typeof window !== 'undefined') {
    track(eventName, properties);
  }
};

// Combined tracking function that sends to all analytics platforms
export const trackEvent = (eventName, properties = {}) => {
  trackPostHog(eventName, properties);
  trackVercel(eventName, properties);
};

// Specific tracking functions for common events
export const trackSignIn = (method) => {
  const eventData = {
    auth_method: method,
    timestamp: new Date().toISOString()
  };

  trackEvent('user_signed_in', eventData);
  
  // Track TikTok registration completion
  if (shouldTrackAnalytics()) {
    trackTikTokCompleteRegistration();
  }
};

export const trackSignOut = () => {
  trackEvent('user_signed_out', { timestamp: new Date().toISOString() });
};

export const trackPageView = (path, referrer = null) => {
  const eventData = {
    path,
    referrer,
    timestamp: new Date().toISOString()
  };

  // PostHog uses $pageview, Vercel uses page_view
  trackPostHog('$pageview', eventData);
  trackVercel('page_view', eventData);
  
  // Track TikTok ViewContent for key pages
  if (shouldTrackAnalytics()) {
    if (path === '/' || path === '/landing') {
      trackTikTokViewContent('Landing Page', 'page');
    } else if (path.includes('auth')) {
      trackTikTokViewContent('Sign In Page', 'page');
    }
  }
};

export const trackButtonClick = (buttonName, location, additionalData = {}) => {
  const eventData = {
    button_name: buttonName,
    location,
    ...additionalData,
    timestamp: new Date().toISOString()
  };

  trackEvent('button_clicked', eventData);
  
  // Track TikTok events for key CTA buttons
  if (shouldTrackAnalytics() && (buttonName.toLowerCase().includes('audit') || buttonName.toLowerCase().includes('cta'))) {
    trackTikTokLead('audit_interest', 'cta_button');
  }
};

export const trackFormSubmission = (formName, success = true) => {
  const eventData = {
    form_name: formName,
    success,
    timestamp: new Date().toISOString()
  };

  trackEvent('form_submitted', eventData);
  
  // Track TikTok lead/contact events for form submissions
  if (shouldTrackAnalytics() && success) {
    if (formName.toLowerCase().includes('contact')) {
      trackTikTokContact('form');
    } else {
      trackTikTokLead('form_submission', formName);
    }
  }
};

export const trackError = (errorType, errorMessage, context = {}) => {
  const eventData = {
    error_type: errorType,
    error_message: errorMessage,
    context,
    timestamp: new Date().toISOString()
  };

  trackEvent('error_occurred', eventData);
}; 